import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCog, Settings, LogOut, CreditCard } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { ThemeToggler } from '../common/ThemeToggler';
import type { PricingPlan } from '../../types/index';

/**
 * Mobile header avatar + dropdown. Surfaces profile, the active plan, and
 * logout — the essentials that live in the desktop sidebar but are otherwise
 * unreachable from the compact mobile header.
 */
export function HeaderProfileMenu() {
  const { user, logout } = useAuth();

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const plan = typeof user.planId === 'object' ? (user.planId as PricingPlan) : null;

  const handleLogout = async () => {
    setOpen(false);
    await logout();
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
        className="tap-target flex items-center justify-center rounded-full transition-transform active:scale-95"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white shadow-sm ring-2 ring-transparent hover:ring-brand-500/30">
          {user.firstName[0]}{user.lastName[0]}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            className="absolute right-0 top-full mt-2 w-72 origin-top-right overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900 z-50"
            role="menu"
          >
            {/* Identity */}
            <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-semibold text-white shadow-sm">
                {user.firstName[0]}{user.lastName[0]}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900 dark:text-zinc-100">{user.fullName}</p>
                <p className="truncate text-xs text-gray-500 dark:text-zinc-400">{user.email}</p>
              </div>
            </div>

            {/* Edit profile */}
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 min-h-[44px] py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800 transition-colors"
              role="menuitem"
            >
              <UserCog className="h-4 w-4 text-gray-400 dark:text-zinc-500" />
              Edit Profile
            </Link>

            {/* Settings */}
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 min-h-[44px] py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800 transition-colors"
              role="menuitem"
            >
              <Settings className="h-4 w-4 text-gray-400 dark:text-zinc-500" />
              Settings
            </Link>

            {/* Theme */}
            <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-4 py-2.5 dark:border-zinc-800">
              <span className="text-sm text-gray-700 dark:text-zinc-200">Theme</span>
              <ThemeToggler />
            </div>

            {/* Plan */}
            <Link
              to="/pricing"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between gap-3 border-t border-gray-100 px-4 min-h-[44px] py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-800 transition-colors"
              role="menuitem"
            >
              <span className="flex items-center gap-3">
                <CreditCard className="h-4 w-4 text-gray-400 dark:text-zinc-500" />
                Plan
              </span>
              <span className="text-xs text-gray-500 dark:text-zinc-400">{plan?.name ?? 'View'}</span>
            </Link>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 border-t border-gray-100 px-4 min-h-[44px] py-2.5 text-sm text-red-600 hover:bg-red-50 dark:border-zinc-800 dark:text-red-400 dark:hover:bg-red-950/30 transition-colors"
              role="menuitem"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
